import React, { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import { Autocomplete, Avatar, Box, Button, CircularProgress, Container, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Employee } from "../../../types/Employee";
import { MedicalPaperClinic, MedicalPaperRequest } from "../../../types/MedicalPaperRequest";
import { getDocs, collection, where, limit, orderBy, getFirestore, query, doc, getDoc, setDoc } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import employeeConverter from '../../../converters/employeeConverter';
import { DateTime } from 'luxon';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';

type Props = {
  employee?: Employee;
  medicalPaperRequest?: MedicalPaperRequest;
  onSaved?: (medicalPaperRequest: MedicalPaperRequest) => void;
  onClosed?: () => void;
}
export const MedicalPaperRequestForm = (props: Props) => {
  const { medicalPaperRequest, onSaved, onClosed } = props;
  const editMode = medicalPaperRequest !== undefined;
  
  const { enqueueSnackbar } = useSnackbar();
  const db = getFirestore();

  const [employee, setEmployee] = useState<Employee | undefined>(props.employee);
  const [clinic, setClinic] = useState<MedicalPaperClinic>(editMode ? medicalPaperRequest.clinic : 'SANAIYAH');
  const [others, setOthers] = useState<string>(editMode && medicalPaperRequest.clinic === 'OTHERS' && medicalPaperRequest.others ? medicalPaperRequest.others : '');
  const [date, setDate] = useState(editMode ? medicalPaperRequest.date : DateTime.now().toFormat('yyyyMMdd'));
 
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [saving, setSaving] = useState(false);

  const [othersTextHelper, setOthersTextHelper] = useState('');

  const searchDebounced = useDebouncedCallback((value: string) => {
    setLoadingEmployees(true);
    setEmployees([]);
    getDocs(
      query(collection(db, 'Employees'), 
      where('idNumber', '>=', value), 
      where('idNumber', '<=', value + '\uf8ff'),
      where('status', '==', 'Active'),
      limit(15),
      orderBy('idNumber'))
    .withConverter(employeeConverter)).then((qSnapshot) => {
      setEmployees(qSnapshot.docs.map((d) => d.data()));
      setLoadingEmployees(false);
    });
  }, 1000);

  const handleOpenAutocomplete = () => setOpenAutocomplete(true);
  const handleCloseAutocomplete = () => setOpenAutocomplete(false);

  const handleTexInputOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebounced(event.currentTarget.value);
  };

  const handleAutocompleteSelectChange = (_e: SyntheticEvent<Element, Event>, value: Employee | null) => {
    setEmployee(value ?? undefined);
  };

  const handleCancelOnClick = () => onClosed && onClosed();

  const handleSaveOnClick = () => {
    // Clear helper texts
    setOthersTextHelper('');

    if (clinic === 'OTHERS' && others.trim() === '') {
      setOthersTextHelper('Please enter a clinic name.');
      return;
    }

    if (employee) {
      if (window.confirm((editMode ? 'Update ' : 'Create ') + ` employee ${employee.idNumber} medical paper request?`)) {
        setSaving(true);

        let request: any = {
          id: uuidv4(),
          idNumber: employee.idNumber,
          name:  employee.name,
          clinic,
          date,
          others: others.trim() === '' ? undefined : others,
          dateRequested: DateTime.utc().toISO(),
          status: 'New',
          lastUpdate: DateTime.utc().toISO(),
        };

        if (editMode) {
          request = {
            id: medicalPaperRequest.id,
            clinic,
            date,
            others: clinic !== 'OTHERS' || others.trim() === '' ? undefined : others,
          }
        }

        // Save
        setDoc(doc(db, `Employees/${employee.idNumber}/MedicalPaperRequests/${request.id}`), request, { merge: true })
          .then(() => {
            enqueueSnackbar(`Employee ${employee.idNumber} medical paper request has been ` + (editMode ? 'updated!' : 'created!'), { variant: 'success' });
              
            setClinic('SANAIYAH');
            setDate(DateTime.now().toFormat('yyyyMMdd'));
            setSaving(false);

            onSaved && onSaved(editMode ? { ...medicalPaperRequest, ...request } : request);
          })
          .catch((error) => {
            enqueueSnackbar(typeof(error) === 'string' ? error : error.message, { variant: 'success' });
          });
      }
    }
  };

  useEffect(() => {
    if (editMode) {
      // Get employee information
      const { idNumber } = medicalPaperRequest;
      if (props.employee === undefined) {
        getDoc(doc(db, `Employees/${idNumber}`).withConverter(employeeConverter)).then(docSnapshot => {
          setEmployee(docSnapshot.data());
        });
      }
    } else {
      if (props.employee) {
        setEmployee(employee);
      }
    }
  }, [editMode]);

  return (
    <Box>
      <Stack spacing={2.5} padding={3}>
        {employee !== undefined ? (
          <Box mb={2}>
            <Stack direction='row' spacing={2}>
              <Avatar src={employee.picture} />
              <Stack>
                <Typography fontWeight='700'>{employee.name}</Typography>
                <Typography variant='caption'>{employee.idNumber}</Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Autocomplete
              id="asynchronous-demo"
              fullWidth
              open={openAutocomplete}
              onOpen={handleOpenAutocomplete}
              onClose={handleCloseAutocomplete}
              isOptionEqualToValue={(option, value) => option.idNumber === value.idNumber}
              getOptionLabel={option => `${option.idNumber} - ${option.name}`}
              options={employees}
              loading={loadingEmployees}
              noOptionsText="Enter employee id number..."
              onChange={handleAutocompleteSelectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Id Number"
                  onChange={handleTexInputOnChanged}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}
        {employee && (
          <>
            <FormControl fullWidth>
              <InputLabel id="medical-request-clinic-label">Type</InputLabel>
              <Select
                labelId="medical-request-clinic-label"
                id="medical-request-clinic-select"
                value={clinic}
                label="Clinic"
                onChange={(evt) => {
                  setClinic(evt.target.value as MedicalPaperClinic);
                }}
              >
                <MenuItem value="SANAIYAH">SANAIYAH</MenuItem>
                <MenuItem value="SALHIYA">SALHIYA</MenuItem>
                <MenuItem value="NUS-A">NUS-A</MenuItem>
                <MenuItem value="OTHERS">OTHERS</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                label="Date"
                format="MMMM dd, yyyy"
                value={DateTime.fromFormat(date, 'yyyyMMdd')}
                onChange={(selectedDate: DateTime | null) => {
                  if (selectedDate !== null) {
                    setDate(selectedDate.toFormat('yyyyMMdd'));
                  }
                }}
              />
            </LocalizationProvider>
            {clinic === 'OTHERS' && (
              <TextField
                label='Clinic Name'
                placeholder='Enter clinic name...'
                value={others}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setOthers(event.target.value);
                }}
                helperText={othersTextHelper}
                error={othersTextHelper !== ''}
              />
            )}
          </>
        )}
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ p: 1 }} direction='row' justifyContent='right'>
        <Button color='error' onClick={handleCancelOnClick}>CANCEL</Button>
        <Button disabled={saving} onClick={handleSaveOnClick}>SAVE</Button>
      </Stack>
    </Box>
  );
};
